import type { CampusInput } from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { campusKeys } from './keys.ts'
import { useAccess } from '../access/useAccess.ts'

/**
 * Campus list + CRUD. The list is cursor-paginated on the wire but campuses
 * are a handful per church, so this hook drains all pages into one array —
 * pickers and settings tables want the full set.
 */
export function useCampuses() {
  const api = useApi()
  // The church's campuses are its settings' (Organization Viewer); nobody else asks.
  const access = useAccess()
  return useQuery({
    queryKey: campusKeys.list(),
    enabled: access.has('org', 'viewer'),
    queryFn: async () => {
      const items = []
      let cursor: string | undefined
      do {
        const { data, error } = await api.api.v1.campuses.get({
          query: cursor ? { cursor } : {},
        })
        if (error) throw error
        items.push(...data.items)
        cursor = data.nextCursor
      } while (cursor)
      return items
    },
  })
}

export function useCreateCampus() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: CampusInput) => {
      const { data, error } = await api.api.v1.campuses.post(input)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: campusKeys.all() }),
  })
}

export function useUpdateCampus() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<CampusInput> & { id: string }) => {
      const { data, error } = await api.api.v1.campuses({ id }).patch(input)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: campusKeys.all() }),
  })
}

export function useDeleteCampus() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.campuses({ id }).delete()
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: campusKeys.all() }),
  })
}
